import React from "react";
import { SERVICES_DATA } from "../data";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import FadeIn from "../components/FadeIn";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import Parallax from "../components/Parallax";

function getOptimizedImage(url, width = 1200) {
  if (!url || !url.includes("assets.auxodev.dk")) return url;
  if (url.includes("/cdn-cgi/image/")) return url;
  return url.replace(
    "https://assets.auxodev.dk/",
    `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
  );
}

export default function Services() {
  const { lang, t } = useLanguage();

  const homePath = lang === "da" ? "/" : "/en";
  const contactPath = lang === "da" ? "/kontakt" : "/en/contact";
  const projectsPath = lang === "da" ? "/projekter" : "/en/projects";
  const aboutPath = lang === "da" ? "/om-os" : "/en/about";

  // Custom SEO titles and descriptions for Services page (strictly <= 512px title and <= 970px description)
  const seoTitle = lang === "da" 
    ? "Byggeydelser & Tømrer | CPH Collective" 
    : "Building Services & Carpentry | CPH Collective";
  const seoDesc = lang === "da"
    ? "Totalentreprise, projektering, tømrerarbejde, renovering og byggepladsservice i København og på Sjælland. Faglige løsninger til erhverv."
    : "Turnkey solutions, engineering, carpentry, renovation, and construction site services in Copenhagen. Professional building services.";

  return (
    <div className="bg-stone-50 text-stone-900 min-h-screen pt-24 md:pt-32">
      {/* SEO Tags */}
      <SEO title={seoTitle} description={seoDesc} />

      {/* Header Section */}
      <section className="py-16 md:py-24 border-b border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <div className="max-w-4xl">
            <Parallax speed={0.04} type="text">
              <FadeIn direction="up">
                <span className="text-xs uppercase tracking-[0.4em] text-stone-500 font-mono block mb-4">
                  {t("services.header.tag")}
                </span>
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-light tracking-tight text-stone-900 uppercase mb-8">
                  {t("services.header.title")}
                </h1>
                <p className="text-stone-600 text-lg md:text-xl font-light leading-relaxed max-w-3xl">
                  {lang === "da" ? (
                    <>
                      <Link
                        to={homePath}
                        className="text-stone-900 font-medium underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 transition-colors"
                      >
                        CPH Collective
                      </Link>{" "}
                      leverer strukturerede byggeydelser af høj kvalitet til erhvervskunder, bygherrer og aktive byggepladser. Vi kombinerer ingeniørmæssig præcision med solidt håndværk.
                    </>
                  ) : (
                    <>
                      <Link
                        to={homePath}
                        className="text-stone-900 font-medium underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 transition-colors"
                      >
                        CPH Collective
                      </Link>{" "}
                      delivers structured, high-quality construction services for commercial clients, developers, and active construction sites. We combine engineering excellence with premium craftsmanship.
                    </>
                  )}
                </p>
              </FadeIn>
            </Parallax>
          </div>

          {/* Service Quick Navigation Bar */}
          <div className="mt-12 pt-8 border-t border-stone-200">
            <span className="text-[11px] uppercase tracking-[0.25em] text-stone-400 font-mono block mb-4">
              {lang === "da" ? "Gå direkte til ydelsesside:" : "Jump directly to service page:"}
            </span>
            <div className="flex flex-wrap gap-2.5">
              {SERVICES_DATA.map((service, idx) => {
                const sSlug = service.slug?.[lang] || service.id;
                const sPath = lang === "da" ? `/ydelser/${sSlug}` : `/en/services/${sSlug}`;
                const sTitle = service.title[lang] || service.title.da;
                return (
                  <Link
                    key={service.id}
                    to={sPath}
                    className="text-xs font-mono px-3.5 py-2 bg-stone-100 hover:bg-stone-900 hover:text-white border border-stone-200 transition-all duration-300 flex items-center gap-1.5 group"
                  >
                    <span className="text-stone-400 group-hover:text-stone-300">0{idx + 1}.</span>
                    <span>{sTitle}</span>
                    <ArrowRight className="w-3 h-3 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Services List - Alternating Editorial Layout */}
      <section className="divide-y divide-stone-200">
        {SERVICES_DATA.map((service, index) => {
          const isEven = index % 2 === 0;
          const title = service.title[lang] || service.title.da;
          const subtitle = service.subtitle?.[lang] || service.subtitle?.da;
          const description = service.description[lang] || service.description.da;
          const includes = service.includes?.[lang] || [];
          const idealFor = service.idealFor?.[lang] || "";
          const seoKeywords = service.seoKeywords?.[lang] || [];
          const sSlug = service.slug?.[lang] || service.id;
          const servicePath = lang === "da" ? `/ydelser/${sSlug}` : `/en/services/${sSlug}`;

          return (
            <div
              key={service.id}
              id={service.id}
              className="py-24 md:py-36 w-full px-6 md:px-12 lg:px-16"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 items-center">
                {/* Image Block with Direct Link */}
                <div
                  className={`lg:col-span-6 ${
                    isEven ? "lg:order-1" : "lg:order-2"
                  }`}
                >
                  <FadeIn direction={isEven ? "right" : "left"}>
                    <Link
                      to={servicePath}
                      className="group block relative h-[400px] md:h-[550px] overflow-hidden border border-stone-200"
                    >
                      <Parallax speed={-0.06} type="background" className="w-full h-full">
                        <img
                          src={getOptimizedImage(service.image, 1200)}
                          alt={`${title} – ${subtitle} | CPH Collective`}
                          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                        />
                      </Parallax>
                      <div className="absolute inset-0 bg-stone-950/20 group-hover:bg-stone-950/35 transition-colors z-10" />
                      <div className="absolute bottom-4 right-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="inline-flex items-center gap-2 bg-white text-stone-900 text-xs font-mono px-3.5 py-2 uppercase tracking-wider shadow-md">
                          <span>{lang === "da" ? "Læs mere" : "Read more"}</span>
                          <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </Link>
                  </FadeIn>
                </div>

                {/* Content Block */}
                <div
                  className={`lg:col-span-6 ${
                    isEven ? "lg:order-2" : "lg:order-1"
                  }`}
                >
                  <FadeIn direction={isEven ? "left" : "right"} delay={150}>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="w-1.5 h-1.5 rounded-full bg-stone-900" />
                      <span className="text-xs uppercase tracking-[0.3em] text-stone-500 font-mono">
                        0{index + 1} / {title}
                      </span>
                    </div>

                    <Link to={servicePath} className="block group">
                      <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-stone-900 uppercase mb-3 group-hover:text-stone-600 transition-colors">
                        {title}
                      </h2>
                    </Link>

                    {subtitle && (
                      <p className="text-sm font-mono text-stone-500 uppercase tracking-wider mb-6">
                        {subtitle}
                      </p>
                    )}

                    <p className="text-stone-700 text-base md:text-lg font-light leading-relaxed mb-8">
                      {description}
                    </p>

                    {/* Includes List */}
                    <div className="mb-8">
                      <h4 className="text-xs uppercase tracking-[0.2em] text-stone-500 font-mono mb-4">
                        {t("services.scope.title")}
                      </h4>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {includes.slice(0, 4).map((item, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-2.5 text-sm text-stone-600 font-light"
                          >
                            <CheckCircle2 className="w-4 h-4 text-stone-500 shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {idealFor && (
                      <p className="text-stone-500 text-sm italic font-light mb-8">
                        {idealFor}
                      </p>
                    )}

                    {/* Action Buttons: Read Dedicated Page + Request Quote */}
                    <div className="flex flex-wrap items-center gap-4 mb-8">
                      <Link
                        to={servicePath}
                        className="inline-flex items-center gap-2.5 text-xs uppercase tracking-[0.2em] font-mono text-white bg-stone-900 hover:bg-stone-800 transition-all duration-300 px-6 py-3.5"
                      >
                        <span>{lang === "da" ? `Læs mere om ${title}` : `Explore ${title}`}</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Link>

                      <Link
                        to={`${contactPath}?service=${encodeURIComponent(title)}`}
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-mono text-stone-700 hover:text-stone-950 bg-stone-100 hover:bg-stone-200 border border-stone-200 transition-all px-5 py-3.5"
                      >
                        <span>{lang === "da" ? "Få et tilbud" : "Request quote"}</span>
                      </Link>
                    </div>

                    {/* SEO Keywords Tag Cloud */}
                    {seoKeywords.length > 0 && (
                      <div className="border-t border-stone-200 pt-6">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-mono block mb-3">
                          {t("services.standards.title")}
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {seoKeywords.map((keyword, idx) => (
                            <span
                              key={idx}
                              className="text-[11px] font-mono text-stone-600 border border-stone-200 px-2.5 py-1 bg-stone-100"
                            >
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </FadeIn>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* Cross-linking Section to other subpages */}
      <section className="py-20 bg-stone-100/70 border-t border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 bg-white border border-stone-200 flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono uppercase tracking-[0.25em] text-stone-400 block mb-3">
                  {lang === "da" ? "Se vores arbejde" : "View our work"}
                </span>
                <h3 className="text-xl font-light uppercase tracking-tight text-stone-900 mb-3">
                  {lang === "da" ? "Gennemførte Referencer" : "Completed Projects"}
                </h3>
                <p className="text-stone-600 text-sm font-light leading-relaxed mb-6">
                  {lang === "da"
                    ? "Udforsk vores seneste byggeprojekter og referencer i København og på Sjælland."
                    : "Explore our latest turnkey and carpentry projects across Copenhagen and Zealand."}
                </p>
              </div>
              <Link
                to={projectsPath}
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-stone-900 hover:text-stone-600 transition-colors border-b border-stone-900 pb-1 self-start"
              >
                <span>{lang === "da" ? "Udforsk projekter" : "Explore projects"}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="p-8 bg-white border border-stone-200 flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono uppercase tracking-[0.25em] text-stone-400 block mb-3">
                  {lang === "da" ? "Faglighed & Standarder" : "Standards & Approach"}
                </span>
                <h3 className="text-xl font-light uppercase tracking-tight text-stone-900 mb-3">
                  {lang === "da" ? "Om CPH Collective" : "About CPH Collective"}
                </h3>
                <p className="text-stone-600 text-sm font-light leading-relaxed mb-6">
                  {lang === "da"
                    ? "Læs om vores værdier, kvalitetssikring og hvorfor bygherrer vælger os som fast partner."
                    : "Learn about our core values, quality assurance, and why clients trust us as their contractor."}
                </p>
              </div>
              <Link
                to={aboutPath}
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-stone-900 hover:text-stone-600 transition-colors border-b border-stone-900 pb-1 self-start"
              >
                <span>{lang === "da" ? "Læs om os" : "Learn about us"}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="p-8 bg-white border border-stone-200 flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono uppercase tracking-[0.25em] text-stone-400 block mb-3">
                  {lang === "da" ? "Næste Skridt" : "Next Step"}
                </span>
                <h3 className="text-xl font-light uppercase tracking-tight text-stone-900 mb-3">
                  {lang === "da" ? "Start et Byggeprojekt" : "Start a Project"}
                </h3>
                <p className="text-stone-600 text-sm font-light leading-relaxed mb-6">
                  {lang === "da"
                    ? "Har du et konkret projekt eller behov for faglig sparring? Kontakt vores byggeledelse."
                    : "Have an upcoming project or need advice? Contact our construction management team."}
                </p>
              </div>
              <Link
                to={contactPath}
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-stone-900 hover:text-stone-600 transition-colors border-b border-stone-900 pb-1 self-start"
              >
                <span>{lang === "da" ? "Kontakt os" : "Contact us"}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-stone-100 py-24 border-t border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16 text-center">
          <div className="max-w-3xl mx-auto">
            <FadeIn direction="up">
              <h2 className="text-3xl md:text-4xl font-light uppercase tracking-tight text-stone-900 mb-6">
                {t("services.cta.title")}
              </h2>
              <p className="text-stone-600 text-base md:text-lg font-light mb-10 leading-relaxed">
                {t("services.cta.text")}
              </p>
              <Link
                to={contactPath}
                className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white bg-stone-900 hover:bg-stone-800 transition-all duration-500 px-8 py-4 font-semibold font-mono"
              >
                {t("cta.requestQuote")}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
}
